/**
 * Which links open in a dialog (SF-T-160): factory pages in the link dialog, web addresses in a
 * framed one, and the rest as ordinary links.
 */

/** A click the browser should keep: a new tab or window (Ctrl/Cmd, Shift, Alt, the middle button). */
export function isModifiedClick(e: { button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }): boolean {
  return e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
}

/** A factory page's path for a link: `/p/sf/runs/3`, or the same for a full address on this site; null for anywhere else. */
export function internalPath(href: string, origin: string): string | null {
  if (href.startsWith('/')) return href.startsWith('//') ? null : href;
  try {
    const url = new URL(href);
    return url.origin === origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
}

/** The project in a page's path (`/p/<slug>/…`), or null. */
function projectSlugOf(pathname: string): string | null {
  const match = /^\/p\/([^/?#]+)/.exec(pathname);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Whether a factory page can open in the link dialog over a page in `baseSlug`'s project (null:
 * the page isn't in one, as Chat isn't). The tab has one current project (lib/current-project.ts),
 * so another project's page, and the master board and Inbox (which make an open task's project the
 * tab's), open in a dialog only over a page outside projects; over a project's page they go there as
 * links do. Chat (the link was in it), signing in, and the API's own addresses (a download) never do.
 */
export function canOpenInDialog(path: string, baseSlug: string | null): boolean {
  const pathname = path.split(/[?#]/, 1)[0];
  if (/^\/(chat|login|accept-invite|api)(\/|$)/.test(pathname)) return false;
  const slug = projectSlugOf(pathname);
  if (slug) return baseSlug == null || slug === baseSlug;
  if (pathname === '/' || /^\/(board|inbox)(\/|$)/.test(pathname)) return baseSlug == null;
  return true;
}

/** A task's own page in a project (`/p/<slug>/board/<task id>`). */
export function isTaskPath(pathname: string): boolean {
  return /^\/p\/[^/]+\/board\/[^/?#]+/.test(pathname);
}

/**
 * Sites that won't show inside a frame (X-Frame-Options or a CSP frame-ancestors), so a link to them
 * opens a new tab, as it did before: the frame would only be blank. Each entry covers its subdomains.
 */
export const FRAME_BLOCKING_HOSTS = [
  'github.com',
  'githubusercontent.com',
  'google.com',
  'youtube.com',
  'asana.com',
  'trello.com',
  'atlassian.net',
  'atlassian.com',
  'linear.app',
  'notion.so',
  'notion.site',
  'slack.com',
  'figma.com',
  'x.com',
  'twitter.com',
  'facebook.com',
  'instagram.com',
  'linkedin.com',
  'stackoverflow.com',
  'cloudflare.com',
  'vercel.com',
  'neon.tech',
  'anthropic.com',
  'claude.ai',
  'openai.com',
  'chatgpt.com',
  'microsoft.com',
  'live.com',
  'office.com',
  'apple.com',
  'amazon.com',
  'aws.amazon.com',
  'stripe.com',
  'paypal.com',
];

/** Whether a web address can be tried in the framed dialog: http(s), and not a site known to refuse frames. */
export function canFrame(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
  const host = url.hostname.toLowerCase();
  return !FRAME_BLOCKING_HOSTS.some((blocked) => host === blocked || host.endsWith(`.${blocked}`));
}
